/**
 * Лента оценки интерьеров на телефоне `/m/interiors` (спека §2). Живёт ВНЕ `AppLayout`: меню и
 * боковая панель на телефоне съели бы экран, а здесь нужен только кадр на весь экран и две
 * кнопки под большой палец. Вход — тот же `Authenticated`, что у панели.
 *
 * Без ожидания: нажатие сразу показывает следующее фото, голос уходит в фоне. Голоса и отмены
 * идут СТРОГО по очереди (`ops`): иначе отмена могла бы обогнать свой голос, и он остался бы на
 * сервере. Логика очереди фото — `lib/interiors.ts`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiFailure } from "@/lib/api";
import {
  advance,
  afterTag,
  excludeIds,
  forget,
  merge,
  needMore,
  undo,
  type InteriorMe,
  type Queue,
  type Verdict,
} from "@/lib/interiors";
import { ackInstruction, loadMe, loadNext, removeVote, sendVote } from "@/lib/interiorsApi";
import { num, thumbUrl } from "@/lib/media";

/** Запрос к серверу: голос или его снятие. */
type Op = { kind: "vote"; id: number; verdict: Verdict } | { kind: "unvote"; id: number };

const EMPTY: Queue = { items: [], last: null, done: false, seen: [] };
/** Сколько кнопки заперты после нажатия: двойной тап не отдаёт два голоса. */
const LOCK_MS = 250;
/** Фон тёмной темы (`--bg` в `[data-theme="dark"]`, globals.css). */
const DARK_BG = "oklch(29.3% 0.016 264)";

export function InteriorsSwipePage() {
  const [me, setMe] = useState<InteriorMe | null>(null);
  const [meError, setMeError] = useState(false);
  const [help, setHelp] = useState(false);
  const [voted, setVoted] = useState(0);
  const [queue, setQueue] = useState<Queue>(EMPTY);
  const [failed, setFailed] = useState<Op[]>([]);
  const [fetchError, setFetchError] = useState(false);
  const [locked, setLocked] = useState(false);
  /** Повод перепроверить «нужно ли ещё»: пока шёл запрос, очередь могла опустеть снова. */
  const [recheck, setRecheck] = useState(0);

  // Зеркало состояния для фоновых ответов: они приходят позже и должны видеть свежую очередь.
  const queueRef = useRef<Queue>(EMPTY);
  const failedRef = useRef<Op[]>([]);
  const lockRef = useRef(false);
  const fetching = useRef(false);
  const ops = useRef<Promise<void>>(Promise.resolve());
  /** id с голосом/отменой в пути или упавшие: сервер их ещё не знает, в выдачу они не должны попасть. */
  const pending = useRef(new Map<number, number>());
  const preloaded = useRef(new Set<number>());

  // Телефон: без pull-to-refresh (Chrome Android перезагружает страницу) и без белой вспышки
  // под страницей (резинка iOS, проверка входа) — тёмный фон на html/body, пока лента открыта.
  useEffect(() => {
    const els = [document.documentElement, document.body];
    const prev = els.map((e) => ({ o: e.style.overscrollBehavior, b: e.style.background }));
    for (const e of els) {
      e.style.overscrollBehavior = "none";
      e.style.background = DARK_BG;
    }
    return () => {
      els.forEach((e, i) => {
        e.style.overscrollBehavior = prev[i]?.o ?? "";
        e.style.background = prev[i]?.b ?? "";
      });
    };
  }, []);

  const apply = useCallback((fn: (q: Queue) => Queue) => {
    queueRef.current = fn(queueRef.current);
    setQueue(queueRef.current);
  }, []);

  const setFailedOps = useCallback((fn: (ops: Op[]) => Op[]) => {
    failedRef.current = fn(failedRef.current);
    setFailed(failedRef.current);
  }, []);

  const fetchMe = useCallback(() => {
    setMeError(false);
    loadMe()
      .then((data) => {
        setMe(data);
        setVoted(data.voted);
      })
      .catch(() => setMeError(true));
  }, []);

  useEffect(fetchMe, [fetchMe]);

  /** Снять одну отметку «в пути» с фото. */
  const release = useCallback((id: number) => {
    const left = (pending.current.get(id) ?? 1) - 1;
    if (left > 0) pending.current.set(id, left);
    else pending.current.delete(id);
  }, []);

  /** Поставить запрос в общую очередь; новый запрос по фото заменяет его упавшие. */
  const enqueue = useCallback(
    (op: Op) => {
      for (const f of failedRef.current) if (f.id === op.id) release(op.id);
      setFailedOps((list) => list.filter((f) => f.id !== op.id));
      pending.current.set(op.id, (pending.current.get(op.id) ?? 0) + 1);
      ops.current = ops.current.then(async () => {
        try {
          const res = op.kind === "vote" ? await sendVote(op.id, op.verdict) : await removeVote(op.id);
          // Счётчик сервера берём, только когда за этим запросом никого нет: иначе он отстаёт.
          if (pending.current.size === 1 && pending.current.get(op.id) === 1) setVoted(res.voted);
        } catch (err) {
          if (op.kind === "vote" && err instanceof ApiFailure && err.code === "NOT_IN_POOL") {
            // Фото вышло из пула: голоса нет и отменять нечего — молча дальше.
            apply((q) => forget(q, op.id));
            setVoted((v) => Math.max(0, v - 1));
          } else {
            setFailedOps((list) => [...list.filter((f) => f.id !== op.id), op]);
            return; // id остаётся в `pending`: голос не дошёл, выдаче его не присылать.
          }
        }
        release(op.id);
      });
    },
    [apply, release, setFailedOps],
  );

  /** Повтор тех же запросов (сервер идемпотентен); `enqueue` снимает упавшие сам. */
  const retry = () => {
    for (const op of [...failedRef.current]) enqueue(op);
  };

  const lock = () => {
    lockRef.current = true;
    setLocked(true);
    window.setTimeout(() => {
      lockRef.current = false;
      setLocked(false);
    }, LOCK_MS);
  };

  const vote = (verdict: Verdict) => {
    const head = queueRef.current.items[0];
    if (!head || lockRef.current) return;
    lock();
    setFetchError(false); // новый голос — повод заново попробовать подгрузку
    apply((q) => advance(q, verdict));
    setVoted((v) => v + 1);
    enqueue({ kind: "vote", id: head.id, verdict });
  };

  const cancelLast = () => {
    const last = queueRef.current.last;
    if (!last || lockRef.current) return;
    lock();
    apply(undo);
    setVoted((v) => Math.max(0, v - 1));
    enqueue({ kind: "unvote", id: last.item.id });
  };

  // Подгрузка: в очереди меньше двух фото — ещё три; один запрос за раз.
  const ready = me !== null && me.ack;
  useEffect(() => {
    if (!ready || fetching.current || fetchError || !needMore(queue)) return;
    fetching.current = true;
    const exclude = [...new Set([...excludeIds(queue), ...pending.current.keys()])];
    loadNext(3, afterTag(queue), exclude)
      .then((res) => apply((q) => merge(q, res.items, res.left)))
      .catch(() => setFetchError(true))
      .finally(() => {
        fetching.current = false;
        setRecheck((n) => n + 1);
      });
  }, [ready, queue, fetchError, recheck, apply]);

  // Картинки хвоста грузятся заранее, чтобы следующее фото появилось сразу.
  useEffect(() => {
    for (const item of queue.items) {
      if (preloaded.current.has(item.id)) continue;
      preloaded.current.add(item.id);
      new Image().src = thumbUrl(item.asset_path);
    }
  }, [queue.items]);

  const start = () => {
    setHelp(false);
    if (me && !me.ack) {
      setMe({ ...me, ack: true });
      // Прохождение помнит сервер; не дошло — инструкция покажется при следующем входе.
      ackInstruction().catch(() => undefined);
    }
  };

  if (!me) {
    return (
      <Shell>
        <div className="grid flex-1 place-items-center px-4 text-center text-[14px] text-fg-muted">
          {meError ? (
            <button type="button" onClick={fetchMe} className="underline">
              не загрузилось, повторить
            </button>
          ) : (
            "Загрузка…"
          )}
        </div>
      </Shell>
    );
  }

  if (!me.ack || help) return <Instruction onStart={start} />;

  const head = queue.items[0];
  const finished = !head && queue.done;

  return (
    <Shell>
      <header className="flex h-11 shrink-0 items-center justify-between px-4 text-[14px] text-fg-muted">
        <span>Интерьеры · оценено {num(voted)}</span>
        <button
          type="button"
          aria-label="Инструкция"
          onClick={() => setHelp(true)}
          className="grid size-8 place-items-center rounded-full border border-border text-[15px] text-fg"
        >
          ?
        </button>
      </header>

      <div className="relative min-h-0 flex-1">
        {head ? (
          <img
            key={head.id}
            src={thumbUrl(head.asset_path)}
            alt={head.tag_name}
            className="absolute inset-0 size-full object-contain"
          />
        ) : (
          <div className="grid size-full place-items-center px-6 text-center text-[16px] text-fg">
            {finished ? (
              `Вы оценили все ${num(me.pool || voted)} фото. Спасибо!`
            ) : fetchError ? (
              <button type="button" onClick={() => setFetchError(false)} className="text-fg-muted underline">
                не загрузилось, повторить
              </button>
            ) : (
              <span className="text-fg-muted">Загрузка…</span>
            )}
          </div>
        )}
      </div>

      <footer
        className="shrink-0 px-3 pt-2"
        style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
      >
        <div className="mb-2 flex min-h-8 items-center justify-between gap-3 text-[14px]">
          <button
            type="button"
            onClick={cancelLast}
            disabled={!queue.last || locked}
            className="h-8 px-1 text-fg-muted disabled:opacity-35"
          >
            ↶ отменить
          </button>
          {failed.length > 0 ? (
            <button type="button" onClick={retry} className="h-8 px-1 text-err underline">
              не сохранилось, повторить
            </button>
          ) : null}
        </div>
        {finished ? null : (
          <div className="grid grid-cols-2 gap-3">
            <VoteButton disabled={!head || locked} onClick={() => vote("fails")} icon="👎" label="Не наш интерьер" />
            <VoteButton disabled={!head || locked} onClick={() => vote("works")} icon="👍" label="Сюда наше платье" />
          </div>
        )}
      </footer>
    </Shell>
  );
}

/** Тёмный экран на всю высоту; на компьютере — колонка по центру. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div data-theme="dark" className="h-[100dvh] overscroll-none bg-bg text-fg">
      <div className="mx-auto flex h-full max-w-[520px] flex-col">{children}</div>
    </div>
  );
}

function VoteButton(props: { disabled: boolean; onClick: () => void; icon: string; label: string }) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className="flex h-[72px] flex-col items-center justify-center gap-0.5 rounded-[var(--radius-card)] border border-border-strong bg-bg-elevated text-[15px] font-medium text-fg active:bg-bg-hover disabled:opacity-50"
    >
      <span aria-hidden="true" className="text-[24px] leading-none">{props.icon}</span>
      {props.label}
    </button>
  );
}

function Instruction({ onStart }: { onStart: () => void }) {
  return (
    <Shell>
      <div
        className="flex flex-1 flex-col justify-center gap-5 px-6"
        style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
      >
        <p className="text-[19px] leading-snug text-fg">Смотрите на интерьер, не на платье. Платье здесь чужое — оно не важно.</p>
        <p className="text-[19px] leading-snug text-fg">Вопрос один: поставили бы мы в этот интерьер НАШЕ платье?</p>
        <button
          type="button"
          onClick={onStart}
          className="mt-4 h-14 w-full rounded-[var(--radius-card)] bg-fg text-[16px] font-medium text-bg"
        >
          Понятно, начать
        </button>
      </div>
    </Shell>
  );
}
